import { isOptionalLayerDisplayFilterValid } from '../../displayFilters/displayFilter';
import { isCapabilityAvailable } from '../../licensing/entitlements';
import {
  isLayerLabelConfigurationValid,
  updateLayerLabelConfiguration,
} from '../../labels/validation';
import { createInitialPopupConfiguration, isPopupField, popupFields } from '../../popups/fields';
import type { LayerPopupConfiguration } from '../../popups/model';
import {
  isLayerPopupConfigurationValid,
  normalisePopupFieldLabel,
  popupConfigurationHasContent,
} from '../../popups/validation';
import { fieldDisplayLabel, isJoinedEffectiveField } from '../../joins/effectiveLayer';
import type { LayerDefinition, LayerId, ProjectModelDraft } from '../../project/model';
import {
  categorisableFields,
  createCategorisedClassification,
  createGraduatedClassification,
  numericFields,
  recolourCategorisedClassification,
  recolourGraduatedClassification,
  restyleGraduatedClassification,
  setGraduatedPaletteReversed,
  updateGraduatedBoundary,
} from '../../styles/classification';
import { normaliseHexColour } from '../../styles/colour';
import { validateSymbolReferences } from '../../symbols/registry';
import { geometryFamiliesForSource } from '../../styles/geometry';
import type { ThematicSymbolDefinition } from '../../styles/model';
import { isCategoricalPaletteId, isSequentialPaletteId } from '../../styles/palettes';
import {
  isVectorLayerStyleValid,
  resetThematicSymbolOverride,
  updateThematicSymbolOverride,
  updateVectorSymbol,
} from '../../styles/validation';
import { pruneSelectedFeaturesForDisplayFilter } from '../../selection/selection';
import { ACTION_OK, actionError, type AppStoreActionContext } from './actionSupport';
import type { AppState, StyleChangeResult } from './appState';
import { layerStateContext, type LayerStateContext } from './layerState';

function projectWithStyle(
  state: AppState,
  context: LayerStateContext,
  style: LayerDefinition['style'],
): ProjectModelDraft | null {
  if (!isVectorLayerStyleValid(style, context.source.fields)) return null;
  const project = {
    ...state.project,
    layers: state.project.layers.map((layer) =>
      layer.id === context.layer.id ? { ...layer, style } : layer,
    ),
  };
  try {
    validateSymbolReferences(project);
  } catch {
    return null;
  }
  return project;
}

function projectWithoutLegendEntryExclusions(
  project: ProjectModelDraft,
  layerId: LayerId,
): ProjectModelDraft {
  if (!project.composition.legend.excludedEntryKeysByLayer[layerId]) return project;
  const { [layerId]: _removed, ...remaining } = project.composition.legend.excludedEntryKeysByLayer;
  void _removed;
  return {
    ...project,
    composition: {
      ...project.composition,
      legend: { ...project.composition.legend, excludedEntryKeysByLayer: remaining },
    },
  };
}

function projectWithLabels(
  state: AppState,
  context: LayerStateContext,
  labels: LayerDefinition['labels'],
): ProjectModelDraft | null {
  if (!isLayerLabelConfigurationValid(labels, context.source.fields)) return null;
  return {
    ...state.project,
    layers: state.project.layers.map((layer) =>
      layer.id === context.layer.id ? { ...layer, labels } : layer,
    ),
  };
}

function projectWithPopup(
  state: AppState,
  context: LayerStateContext,
  popup: LayerPopupConfiguration,
): ProjectModelDraft | null {
  if (!isLayerPopupConfigurationValid(popup, context.source.fields)) return null;
  return {
    ...state.project,
    layers: state.project.layers.map((layer) =>
      layer.id === context.layer.id ? { ...layer, popup } : layer,
    ),
  };
}

function projectWithDisplayFilter(
  state: AppState,
  context: LayerStateContext,
  displayFilter: Parameters<AppState['setLayerDisplayFilter']>[1],
): ProjectModelDraft | null {
  if (!isOptionalLayerDisplayFilterValid(displayFilter, context.source.fields)) return null;
  return {
    ...state.project,
    layers: state.project.layers.map((layer) =>
      layer.id === context.layer.id ? { ...layer, displayFilter } : layer,
    ),
  };
}

function popupCapabilityError(state: AppState): StyleChangeResult | null {
  return isCapabilityAvailable(state.entitlement, 'popups.basic')
    ? null
    : actionError('Popup authoring is unavailable.');
}

function colourOverride(
  symbol: ThematicSymbolDefinition,
  colour: string,
): ThematicSymbolDefinition | null {
  const point = updateThematicSymbolOverride(symbol, {
    geometry: 'point',
    values: { fillColor: colour },
  });
  const line = point
    ? updateThematicSymbolOverride(point, { geometry: 'line', values: { color: colour } })
    : null;
  return line
    ? updateThematicSymbolOverride(line, {
        geometry: 'polygon',
        values: { fillColor: colour },
      })
    : null;
}

function preserveGraduatedLegendLabels(
  current: Extract<LayerDefinition['style']['classification'], { mode: 'graduated' }>,
  generated: Extract<LayerDefinition['style']['classification'], { mode: 'graduated' }>,
): typeof generated {
  return {
    ...generated,
    classes: generated.classes.map((numericClass, index) => ({
      ...numericClass,
      symbol: {
        ...numericClass.symbol,
        ...(current.classes[index]?.symbol.legendLabel
          ? { legendLabel: current.classes[index].symbol.legendLabel }
          : {}),
      },
    })),
    nullSymbol: {
      ...generated.nullSymbol,
      ...(current.nullSymbol.legendLabel ? { legendLabel: current.nullSymbol.legendLabel } : {}),
    },
  };
}

export type PresentationActions = Pick<
  AppState,
  | 'setLayerLabelsEnabled'
  | 'setLayerLabelFilter'
  | 'setLayerDisplayFilter'
  | 'updateLayerLabels'
  | 'setLayerPopupsEnabled'
  | 'setLayerPopupTitleField'
  | 'setLayerPopupFieldVisibility'
  | 'setLayerPopupFieldsVisible'
  | 'setLayerPopupFieldLabel'
  | 'moveLayerPopupField'
  | 'setLayerCategoryColour'
  | 'setLayerClassColour'
  | 'setLayerClassificationField'
  | 'setLayerClassificationMode'
  | 'setLayerGraduatedClassCount'
  | 'setLayerGraduatedMethod'
  | 'setLayerGraduatedVisualVariable'
  | 'setLayerGraduatedBoundary'
  | 'setLayerGraduatedPaletteReversed'
  | 'resetLayerGraduatedBreaks'
  | 'setLayerPalette'
  | 'updateLayerCategoryOverride'
  | 'resetLayerCategoryOverride'
  | 'updateLayerClassOverride'
  | 'resetLayerClassOverride'
  | 'setLayerThematicLegendLabel'
  | 'updateLayerSymbol'
>;

export function createPresentationActions({
  get,
  set,
}: AppStoreActionContext): PresentationActions {
  return {
    setLayerLabelsEnabled: (layerId, enabled) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const field = context.layer.labels.field;
      if (enabled && field === null) {
        return actionError('Choose a label field before turning labels on.');
      }
      const labels = { ...context.layer.labels, enabled, field };
      const project = projectWithLabels(state, context, labels);
      if (!project) return actionError('The label configuration is invalid.');
      set({ project });
      return ACTION_OK;
    },
    setLayerLabelFilter: (layerId, filter) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'labels.filter')) {
        return actionError('Label filtering is unavailable.');
      }
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const labels = { ...context.layer.labels, filter };
      const project = projectWithLabels(state, context, labels);
      if (!project) return actionError('The label filter is invalid.');
      set({ project });
      return ACTION_OK;
    },
    setLayerDisplayFilter: (layerId, displayFilter) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'data.displayFilter')) {
        return actionError('Display Filter authoring requires Placeio Maps Pro.');
      }
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const project = projectWithDisplayFilter(state, context, displayFilter);
      if (!project) return actionError('The display filter is invalid.');
      const nextLayer = project.layers.find((layer) => layer.id === layerId);
      if (!nextLayer) return actionError('The selected layer is unavailable.');
      set({
        project,
        shell: {
          ...state.shell,
          selectedFeatures: pruneSelectedFeaturesForDisplayFilter(
            state.shell.selectedFeatures,
            nextLayer,
            context.source,
          ),
          inspectedFeature:
            state.shell.inspectedFeature?.layerId === layerId ? null : state.shell.inspectedFeature,
        },
      });
      return ACTION_OK;
    },
    updateLayerLabels: (layerId, patch) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const labels = updateLayerLabelConfiguration(
        context.layer.labels,
        patch,
        context.source.fields,
      );
      if (!labels) return actionError('The label value is invalid.');
      const project = projectWithLabels(state, context, labels);
      if (!project) return actionError('The label configuration is invalid.');
      set({ project });
      return ACTION_OK;
    },
    setLayerPopupsEnabled: (layerId, enabled) => {
      const state = get();
      const capabilityError = popupCapabilityError(state);
      if (capabilityError) return capabilityError;
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const shouldInitialise =
        enabled &&
        context.layer.popup.fields.length === 0 &&
        context.layer.popup.titleField === null;
      const initial = shouldInitialise
        ? createInitialPopupConfiguration(context.source.fields)
        : context.layer.popup;
      if (enabled && initial.fields.length === 0 && initial.titleField === null) {
        return actionError('This layer has no suitable scalar fields for popups.');
      }
      const popup = {
        ...context.layer.popup,
        enabled,
        titleField: initial.titleField,
        fields: initial.fields,
      };
      const project = projectWithPopup(state, context, popup);
      if (!project) return actionError('The popup configuration is invalid.');
      set({
        project,
        shell: {
          ...state.shell,
          inspectedFeature:
            !enabled && state.shell.inspectedFeature?.layerId === layerId
              ? null
              : state.shell.inspectedFeature,
        },
      });
      return ACTION_OK;
    },
    setLayerPopupTitleField: (layerId, field) => {
      const state = get();
      const capabilityError = popupCapabilityError(state);
      if (capabilityError) return capabilityError;
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      if (field !== null && !isPopupField(context.source.fields, field)) {
        return actionError('Choose a suitable scalar title field.');
      }
      const fields =
        field !== null && field !== context.layer.popup.titleField
          ? context.layer.popup.fields.map((entry) =>
              entry.field === field ? { ...entry, visible: false } : entry,
            )
          : context.layer.popup.fields;
      const popup = {
        ...context.layer.popup,
        titleField: field,
        fields,
      };
      const project = projectWithPopup(state, context, popup);
      if (!project) return actionError('The popup title field is invalid.');
      set({
        project,
        shell: {
          ...state.shell,
          inspectedFeature:
            state.shell.inspectedFeature?.layerId === layerId &&
            !popupConfigurationHasContent(popup)
              ? null
              : state.shell.inspectedFeature,
        },
      });
      return ACTION_OK;
    },
    setLayerPopupFieldVisibility: (layerId, field, visible) => {
      const state = get();
      const capabilityError = popupCapabilityError(state);
      if (capabilityError) return capabilityError;
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const effectiveField = popupFields(context.source.fields).find(
        (candidate) => candidate.name === field,
      );
      if (!effectiveField) {
        return actionError('The popup field is unavailable.');
      }
      const configured = context.layer.popup.fields.some((entry) => entry.field === field);
      const nextFields = !configured
        ? visible
          ? [
              ...context.layer.popup.fields,
              { field, label: fieldDisplayLabel(effectiveField), visible: true },
            ]
          : context.layer.popup.fields
        : !visible && isJoinedEffectiveField(effectiveField)
          ? context.layer.popup.fields.filter((entry) => entry.field !== field)
          : context.layer.popup.fields.map((entry) =>
              entry.field === field ? { ...entry, visible } : entry,
            );
      const popup = {
        ...context.layer.popup,
        fields: nextFields,
      };
      const project = projectWithPopup(state, context, popup);
      if (!project) return actionError('The popup field visibility is invalid.');
      set({
        project,
        shell: {
          ...state.shell,
          inspectedFeature:
            state.shell.inspectedFeature?.layerId === layerId &&
            !popupConfigurationHasContent(popup)
              ? null
              : state.shell.inspectedFeature,
        },
      });
      return ACTION_OK;
    },
    setLayerPopupFieldsVisible: (layerId, visible) => {
      const state = get();
      const capabilityError = popupCapabilityError(state);
      if (capabilityError) return capabilityError;
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const existingByField = new Map(
        context.layer.popup.fields.map((entry) => [entry.field, entry]),
      );
      const fields = visible
        ? popupFields(context.source.fields).map((field) => ({
            field: field.name,
            label: existingByField.get(field.name)?.label ?? fieldDisplayLabel(field),
            visible: true,
          }))
        : context.layer.popup.fields.flatMap((entry) => {
            const field = context.source.fields.find((candidate) => candidate.name === entry.field);
            return field && isJoinedEffectiveField(field) ? [] : [{ ...entry, visible: false }];
          });
      const popup = {
        ...context.layer.popup,
        fields,
      };
      const project = projectWithPopup(state, context, popup);
      if (!project) return actionError('The popup field visibility is invalid.');
      set({
        project,
        shell: {
          ...state.shell,
          inspectedFeature:
            state.shell.inspectedFeature?.layerId === layerId &&
            !popupConfigurationHasContent(popup)
              ? null
              : state.shell.inspectedFeature,
        },
      });
      return ACTION_OK;
    },
    setLayerPopupFieldLabel: (layerId, field, label) => {
      const state = get();
      const capabilityError = popupCapabilityError(state);
      if (capabilityError) return capabilityError;
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      if (!context.layer.popup.fields.some((entry) => entry.field === field)) {
        return actionError('The popup field is unavailable.');
      }
      const project = projectWithPopup(state, context, {
        ...context.layer.popup,
        fields: context.layer.popup.fields.map((entry) =>
          entry.field === field
            ? {
                ...entry,
                label: normalisePopupFieldLabel(
                  label,
                  fieldDisplayLabel(
                    context.source.fields.find((candidate) => candidate.name === entry.field) ?? {
                      name: entry.field,
                      type: 'unknown',
                      nullable: true,
                      presentCount: 0,
                      nullCount: 0,
                    },
                  ),
                ),
              }
            : entry,
        ),
      });
      if (!project) return actionError('The popup field label is invalid.');
      set({ project });
      return ACTION_OK;
    },
    moveLayerPopupField: (layerId, field, direction) => {
      const state = get();
      const capabilityError = popupCapabilityError(state);
      if (capabilityError) return capabilityError;
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const currentIndex = context.layer.popup.fields.findIndex((entry) => entry.field === field);
      const targetIndex = currentIndex + direction;
      if (
        currentIndex < 0 ||
        !Number.isInteger(direction) ||
        (direction !== -1 && direction !== 1) ||
        targetIndex < 0 ||
        targetIndex >= context.layer.popup.fields.length
      ) {
        return actionError('The popup field cannot move in that direction.');
      }
      const fields = [...context.layer.popup.fields];
      const [entry] = fields.splice(currentIndex, 1);
      if (!entry) return actionError('The popup field is unavailable.');
      fields.splice(targetIndex, 0, entry);
      const project = projectWithPopup(state, context, { ...context.layer.popup, fields });
      if (!project) return actionError('The popup field order is invalid.');
      set({ project });
      return ACTION_OK;
    },
    setLayerCategoryColour: (layerId, categoryIndex, colour) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      const normalisedColour = normaliseHexColour(colour);
      if (!context || !normalisedColour) return actionError('Choose a valid hex colour.');
      const classification = context.layer.style.classification;
      if (classification.mode !== 'categorised') {
        return actionError('This layer is not using Categorised styling.');
      }
      if (
        categoryIndex !== 'other' &&
        (!Number.isInteger(categoryIndex) || !classification.categories[categoryIndex])
      ) {
        return actionError('Choose a valid category.');
      }
      const currentSymbol =
        categoryIndex === 'other'
          ? classification.other
          : classification.categories[categoryIndex]?.symbol;
      const nextSymbol = currentSymbol ? colourOverride(currentSymbol, normalisedColour) : null;
      if (!nextSymbol) return actionError('The category colour change was invalid.');
      const nextClassification =
        categoryIndex === 'other'
          ? { ...classification, other: nextSymbol }
          : {
              ...classification,
              categories: classification.categories.map((category, index) =>
                index === categoryIndex ? { ...category, symbol: nextSymbol } : category,
              ),
            };
      const project = projectWithStyle(state, context, {
        ...context.layer.style,
        classification: nextClassification,
      });
      if (!project) return actionError('The category colour change was invalid.');
      set({ project });
      return ACTION_OK;
    },
    setLayerClassColour: (layerId, classIndex, colour) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      const normalisedColour = normaliseHexColour(colour);
      if (!context || !normalisedColour) return actionError('Choose a valid hex colour.');
      const classification = context.layer.style.classification;
      if (classification.mode !== 'graduated') {
        return actionError('This layer is not using Graduated styling.');
      }
      if (
        classIndex !== 'null' &&
        (!Number.isInteger(classIndex) || !classification.classes[classIndex])
      ) {
        return actionError('Choose a valid class.');
      }
      const currentSymbol =
        classIndex === 'null'
          ? classification.nullSymbol
          : classification.classes[classIndex]?.symbol;
      const nextSymbol = currentSymbol ? colourOverride(currentSymbol, normalisedColour) : null;
      if (!nextSymbol) return actionError('The class colour change was invalid.');
      const nextClassification =
        classIndex === 'null'
          ? { ...classification, nullSymbol: nextSymbol }
          : {
              ...classification,
              classes: classification.classes.map((numericClass, index) =>
                index === classIndex ? { ...numericClass, symbol: nextSymbol } : numericClass,
              ),
            };
      const project = projectWithStyle(state, context, {
        ...context.layer.style,
        classification: nextClassification,
      });
      if (!project) return actionError('The class colour change was invalid.');
      set({ project });
      return ACTION_OK;
    },
    setLayerClassificationField: (layerId, fieldName) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const current = context.layer.style.classification;
      const generated =
        current.mode === 'categorised'
          ? createCategorisedClassification(context.source, fieldName, current.paletteId)
          : current.mode === 'graduated'
            ? createGraduatedClassification(
                context.source,
                fieldName,
                current.classCount,
                current.paletteId,
                current.resetMethod,
                current.visualVariable,
                current.paletteReversed,
              )
            : null;
      if (!generated) return actionError('Choose a thematic classification first.');
      if (!generated.ok) return actionError(generated.error.message);
      const project = projectWithStyle(state, context, {
        ...context.layer.style,
        classification:
          current.mode === 'graduated' && generated.value.mode === 'graduated'
            ? preserveGraduatedLegendLabels(current, generated.value)
            : generated.value,
      });
      if (!project) return actionError('The selected field is not valid for this style.');
      set({ project: projectWithoutLegendEntryExclusions(project, layerId) });
      return ACTION_OK;
    },
    setLayerClassificationMode: (layerId, mode) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      if (context.layer.style.classification.mode === mode) return ACTION_OK;

      if (mode === 'single') {
        const project = projectWithStyle(state, context, {
          ...context.layer.style,
          classification: { mode: 'single' },
        });
        if (!project) return actionError('Single-symbol styling could not be applied.');
        set({
          project,
          shell: {
            ...state.shell,
            expandedLayerIds: state.shell.expandedLayerIds.filter((id) => id !== layerId),
          },
        });
        return ACTION_OK;
      }

      const fields =
        mode === 'categorised'
          ? categorisableFields(context.source.fields)
          : numericFields(context.source.fields);
      if (fields.length === 0) {
        return actionError(
          mode === 'categorised'
            ? 'This layer has no suitable scalar fields for Categorised styling.'
            : 'This layer has no numeric fields for Graduated styling.',
        );
      }

      let lastMessage = 'No suitable field could be classified.';
      for (const field of fields) {
        const generated =
          mode === 'categorised'
            ? createCategorisedClassification(context.source, field.name)
            : createGraduatedClassification(context.source, field.name);
        if (!generated.ok) {
          lastMessage = generated.error.message;
          continue;
        }
        const project = projectWithStyle(state, context, {
          ...context.layer.style,
          classification: generated.value,
        });
        if (!project) continue;
        set({ project: projectWithoutLegendEntryExclusions(project, layerId) });
        return ACTION_OK;
      }
      return actionError(lastMessage);
    },
    setLayerGraduatedClassCount: (layerId, classCount) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const classification = context.layer.style.classification;
      if (classification.mode !== 'graduated') {
        return actionError('This layer is not using Graduated styling.');
      }
      const generated = createGraduatedClassification(
        context.source,
        classification.field,
        classCount,
        classification.paletteId,
        classification.resetMethod,
        classification.visualVariable,
        classification.paletteReversed,
      );
      if (!generated.ok) return actionError(generated.error.message);
      const project = projectWithStyle(state, context, {
        ...context.layer.style,
        classification: preserveGraduatedLegendLabels(classification, generated.value),
      });
      if (!project) return actionError('The class count change was invalid.');
      set({ project: projectWithoutLegendEntryExclusions(project, layerId) });
      return ACTION_OK;
    },
    setLayerGraduatedMethod: (layerId, method) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const classification = context.layer.style.classification;
      if (classification.mode !== 'graduated') {
        return actionError('This layer is not using Graduated styling.');
      }
      const generated = createGraduatedClassification(
        context.source,
        classification.field,
        classification.classCount,
        classification.paletteId,
        method,
        classification.visualVariable,
        classification.paletteReversed,
      );
      if (!generated.ok) return actionError(generated.error.message);
      const project = projectWithStyle(state, context, {
        ...context.layer.style,
        classification: preserveGraduatedLegendLabels(classification, generated.value),
      });
      if (!project) return actionError('The classification method was invalid.');
      set({ project: projectWithoutLegendEntryExclusions(project, layerId) });
      return ACTION_OK;
    },
    setLayerGraduatedVisualVariable: (layerId, visualVariable) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const classification = context.layer.style.classification;
      if (classification.mode !== 'graduated') {
        return actionError('This layer is not using Graduated styling.');
      }
      const families = geometryFamiliesForSource(context.source);
      if (
        (visualVariable.kind === 'point-size' &&
          (families.length !== 1 || families[0] !== 'point')) ||
        (visualVariable.kind === 'line-width' && (families.length !== 1 || families[0] !== 'line'))
      ) {
        return actionError('This variation is not valid for the layer geometry.');
      }
      const nextClassification = restyleGraduatedClassification(
        classification,
        classification.paletteId,
        visualVariable,
      );
      const project = projectWithStyle(state, context, {
        ...context.layer.style,
        classification: nextClassification,
      });
      if (!project) return actionError('The graduated variation was invalid.');
      set({ project });
      return ACTION_OK;
    },
    setLayerGraduatedBoundary: (layerId, boundaryIndex, boundary) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const classification = context.layer.style.classification;
      if (classification.mode !== 'graduated') {
        return actionError('This layer is not using Graduated styling.');
      }
      const nextClassification = updateGraduatedBoundary(classification, boundaryIndex, boundary);
      if (!nextClassification) {
        return actionError('A boundary must remain between its neighbouring values.');
      }
      const project = projectWithStyle(state, context, {
        ...context.layer.style,
        classification: nextClassification,
      });
      if (!project) return actionError('The manual boundary was invalid.');
      set({ project });
      return ACTION_OK;
    },
    setLayerGraduatedPaletteReversed: (layerId, reversed) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const classification = context.layer.style.classification;
      if (classification.mode !== 'graduated') {
        return actionError('This layer is not using Graduated styling.');
      }
      const project = projectWithStyle(state, context, {
        ...context.layer.style,
        classification: setGraduatedPaletteReversed(classification, reversed),
      });
      if (!project) return actionError('The palette direction was invalid.');
      set({ project });
      return ACTION_OK;
    },
    resetLayerGraduatedBreaks: (layerId) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const classification = context.layer.style.classification;
      if (classification.mode !== 'graduated') {
        return actionError('This layer is not using Graduated styling.');
      }
      const generated = createGraduatedClassification(
        context.source,
        classification.field,
        classification.classCount,
        classification.paletteId,
        classification.resetMethod,
        classification.visualVariable,
        classification.paletteReversed,
      );
      if (!generated.ok) return actionError(generated.error.message);
      const project = projectWithStyle(state, context, {
        ...context.layer.style,
        classification: preserveGraduatedLegendLabels(classification, generated.value),
      });
      if (!project) return actionError('The generated breaks were invalid.');
      set({ project: projectWithoutLegendEntryExclusions(project, layerId) });
      return ACTION_OK;
    },
    setLayerPalette: (layerId, paletteId) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const classification = context.layer.style.classification;
      const nextClassification =
        classification.mode === 'categorised' && isCategoricalPaletteId(paletteId)
          ? recolourCategorisedClassification(classification, paletteId)
          : classification.mode === 'graduated' && isSequentialPaletteId(paletteId)
            ? recolourGraduatedClassification(classification, paletteId)
            : null;
      if (!nextClassification) return actionError('Choose a palette for the current style.');
      const project = projectWithStyle(state, context, {
        ...context.layer.style,
        classification: nextClassification,
      });
      if (!project) return actionError('The palette change was invalid.');
      set({ project });
      return ACTION_OK;
    },
    updateLayerCategoryOverride: (layerId, categoryIndex, patch) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const classification = context.layer.style.classification;
      if (classification.mode !== 'categorised') {
        return actionError('This layer is not using Categorised styling.');
      }
      const current =
        categoryIndex === 'other'
          ? classification.other
          : classification.categories[categoryIndex]?.symbol;
      if (!current) return actionError('Choose a valid category.');
      const next = updateThematicSymbolOverride(current, patch);
      if (!next) return actionError('The category symbol value is invalid.');
      const nextClassification =
        categoryIndex === 'other'
          ? { ...classification, other: next }
          : {
              ...classification,
              categories: classification.categories.map((category, index) =>
                index === categoryIndex ? { ...category, symbol: next } : category,
              ),
            };
      const project = projectWithStyle(state, context, {
        ...context.layer.style,
        classification: nextClassification,
      });
      if (!project) return actionError('The category symbol change was invalid.');
      set({ project });
      return ACTION_OK;
    },
    resetLayerCategoryOverride: (layerId, categoryIndex) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const classification = context.layer.style.classification;
      if (classification.mode !== 'categorised') {
        return actionError('This layer is not using Categorised styling.');
      }
      if (categoryIndex !== 'other' && !classification.categories[categoryIndex]) {
        return actionError('Choose a valid category.');
      }
      const nextClassification =
        categoryIndex === 'other'
          ? { ...classification, other: resetThematicSymbolOverride(classification.other) }
          : {
              ...classification,
              categories: classification.categories.map((category, index) =>
                index === categoryIndex
                  ? { ...category, symbol: resetThematicSymbolOverride(category.symbol) }
                  : category,
              ),
            };
      const project = projectWithStyle(state, context, {
        ...context.layer.style,
        classification: nextClassification,
      });
      if (!project) return actionError('The category reset was invalid.');
      set({ project });
      return ACTION_OK;
    },
    updateLayerClassOverride: (layerId, classIndex, patch) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const classification = context.layer.style.classification;
      if (classification.mode !== 'graduated') {
        return actionError('This layer is not using Graduated styling.');
      }
      const current =
        classIndex === 'null'
          ? classification.nullSymbol
          : classification.classes[classIndex]?.symbol;
      if (!current) return actionError('Choose a valid class.');
      const next = updateThematicSymbolOverride(current, patch);
      if (!next) return actionError('The class symbol value is invalid.');
      const nextClassification =
        classIndex === 'null'
          ? { ...classification, nullSymbol: next }
          : {
              ...classification,
              classes: classification.classes.map((numericClass, index) =>
                index === classIndex ? { ...numericClass, symbol: next } : numericClass,
              ),
            };
      const project = projectWithStyle(state, context, {
        ...context.layer.style,
        classification: nextClassification,
      });
      if (!project) return actionError('The class symbol change was invalid.');
      set({ project });
      return ACTION_OK;
    },
    resetLayerClassOverride: (layerId, classIndex) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const classification = context.layer.style.classification;
      if (classification.mode !== 'graduated') {
        return actionError('This layer is not using Graduated styling.');
      }
      if (classIndex !== 'null' && !classification.classes[classIndex]) {
        return actionError('Choose a valid class.');
      }
      const nextClassification =
        classIndex === 'null'
          ? {
              ...classification,
              nullSymbol: resetThematicSymbolOverride(classification.nullSymbol),
            }
          : {
              ...classification,
              classes: classification.classes.map((numericClass, index) =>
                index === classIndex
                  ? { ...numericClass, symbol: resetThematicSymbolOverride(numericClass.symbol) }
                  : numericClass,
              ),
            };
      const project = projectWithStyle(state, context, {
        ...context.layer.style,
        classification: nextClassification,
      });
      if (!project) return actionError('The class reset was invalid.');
      set({ project });
      return ACTION_OK;
    },
    setLayerThematicLegendLabel: (layerId, entry, label) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const classification = context.layer.style.classification;
      const legendLabel = label.trim();
      if (legendLabel.length > 120) {
        return actionError('Legend labels must be 120 characters or fewer.');
      }
      const withLabel = (symbol: ThematicSymbolDefinition): ThematicSymbolDefinition => {
        const { legendLabel: _current, ...withoutLabel } = symbol;
        void _current;
        return legendLabel ? { ...withoutLabel, legendLabel } : withoutLabel;
      };
      let nextClassification: typeof classification;
      if (classification.mode === 'categorised') {
        if (entry === 'null') return actionError('Choose a valid category.');
        if (entry === 'other') {
          nextClassification = { ...classification, other: withLabel(classification.other) };
        } else if (classification.categories[entry]) {
          nextClassification = {
            ...classification,
            categories: classification.categories.map((category, index) =>
              index === entry ? { ...category, symbol: withLabel(category.symbol) } : category,
            ),
          };
        } else return actionError('Choose a valid category.');
      } else if (classification.mode === 'graduated') {
        if (entry === 'other') return actionError('Choose a valid class.');
        if (entry === 'null') {
          nextClassification = {
            ...classification,
            nullSymbol: withLabel(classification.nullSymbol),
          };
        } else if (classification.classes[entry]) {
          nextClassification = {
            ...classification,
            classes: classification.classes.map((numericClass, index) =>
              index === entry
                ? { ...numericClass, symbol: withLabel(numericClass.symbol) }
                : numericClass,
            ),
          };
        } else return actionError('Choose a valid class.');
      } else return actionError('This layer is not using thematic styling.');
      const project = projectWithStyle(state, context, {
        ...context.layer.style,
        classification: nextClassification,
      });
      if (!project) return actionError('The legend label was invalid.');
      set({ project });
      return ACTION_OK;
    },
    updateLayerSymbol: (layerId, patch) => {
      const state = get();
      const context = layerStateContext(state, layerId);
      if (!context) return actionError('The selected layer is unavailable.');
      const style = updateVectorSymbol(context.layer.style, patch);
      if (!style) return actionError('The symbol value is invalid.');
      const project = projectWithStyle(state, context, style);
      if (!project) return actionError('The symbol change was invalid.');
      set({ project });
      return ACTION_OK;
    },
  };
}
